import React, { useState, useEffect } from 'react';
import { FaUsers, FaPlus, FaEdit, FaTrash, FaSearch, FaUserGraduate, FaChalkboardTeacher, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import { validateForm } from '../../utils/validators';

/**
 * Class management component for school administrators
 * Allows creating, editing, and managing classes and their students
 */
const ClassManagement = () => {
  const { showSuccess, showError } = useToast();

  // Classes state
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'
  const [showForm, setShowForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedClasses, setExpandedClasses] = useState({});
  const [schoolId, setSchoolId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    grade: '',
    academicYear: '',
    capacity: '',
    classTeacher: '',
    subjects: [],
    description: ''
  });
  
  // Student management state
  const [students, setStudents] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  
  // Form errors
  const [formErrors, setFormErrors] = useState({});

  // Teacher list for dropdown
  const [teachers, setTeachers] = useState([]);

  // Fetch classes on component mount
  useEffect(() => {
    fetchClasses();
    fetchTeachers();
  }, []);

  // Fetch classes from API
  const fetchClasses = async () => {
    try {
      setLoading(true);
      
      // Get school ID using a more robust approach
      const schoolId = await getSchoolId();
      setSchoolId(schoolId);
      
      // Use the school ID to fetch classes
      const response = await api.get(`/schools/${schoolId}/classes`);
      setClasses(response.data.data || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
      showError('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  // Fetch teachers for dropdown
  const fetchTeachers = async () => {
    try {
      // Get school ID using a more robust approach
      const schoolId = await getSchoolId();
      
      // Use the school ID to fetch teachers
      const response = await api.get(`/schools/${schoolId}/teachers`);
      setTeachers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  // Helper function to get school ID
  const getSchoolId = async () => {
    // First, try to get from dashboard
    try {
      const schoolResponse = await api.get('/schools/dashboard');
      console.log('School dashboard response:', schoolResponse.data);
      
      const schoolData = schoolResponse.data.data;
      
      if (schoolData && schoolData.schoolInfo && schoolData.schoolInfo._id) {
        return schoolData.schoolInfo._id;
      } else if (schoolData && schoolData.school && schoolData.school._id) {
        return schoolData.school._id;
      }
    } catch (error) {
      console.error('Error getting school ID from dashboard:', error);
    }
    
    // If not found in dashboard, try user data
    try {
      const userResponse = await api.get('/auth/me');
      const userData = userResponse.data.data;
      if (userData && userData.school) {
        return userData.school;
      }
    } catch (error) {
      console.error('Error getting school ID from user data:', error);
    }
    
    throw new Error('Could not determine school ID');
  };

  // Fetch class students
  const fetchClassStudents = async (classId) => {
    try {
      const response = await api.get(`/schools/classes/${classId}/students`);
      setStudents(response.data.data || []);
    } catch (error) {
      console.error('Error fetching class students:', error);
      showError('Failed to load students');
    }
  };

  // Fetch available students not in the class
  const fetchAvailableStudents = async (classId) => {
    try {
      const response = await api.get(`/schools/students/available?classId=${classId}`);
      setAvailableStudents(response.data.data || []);
    } catch (error) {
      console.error('Error fetching available students:', error);
    }
  };

  // Filter classes based on search term
  const filteredClasses = searchTerm
    ? classes.filter(
        classItem =>
          classItem.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          classItem.grade.toLowerCase().includes(searchTerm.toLowerCase()) ||
          classItem.academicYear.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : classes;

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : parseInt(value)) : value
    }));
    
    // Clear the error for this field if there was one
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle student search input change
  const handleStudentSearchChange = (e) => {
    setStudentSearchTerm(e.target.value);
  };

  // Toggle class details
  const toggleClassDetails = (classId) => {
    setExpandedClasses(prev => ({
      ...prev,
      [classId]: !prev[classId]
    }));
  };

  // Open add form
  const handleAddNew = () => {
    setFormData({
      name: '',
      grade: '',
      academicYear: '',
      capacity: '',
      classTeacher: '',
      subjects: [],
      description: ''
    });
    setFormMode('add');
    setShowForm(true);
    setFormErrors({});
  };

  // Open edit form
  const handleEdit = (classItem) => {
    setSelectedClass(classItem);
    setFormData({
      name: classItem.name || '',
      grade: classItem.grade || '',
      academicYear: classItem.academicYear || '',
      capacity: classItem.capacity || '',
      classTeacher: classItem.classTeacher?._id || '',
      subjects: classItem.subjects || [],
      description: classItem.description || ''
    });
    setFormMode('edit');
    setShowForm(true);
    setFormErrors({});
  };

  // Open delete confirmation
  const handleDeleteClick = (classItem) => {
    setSelectedClass(classItem);
    setShowDeleteModal(true);
  };

  // Delete class
  const handleDeleteConfirm = async () => {
    if (!selectedClass) return;
    
    try {
      await api.delete(`/schools/classes/${selectedClass._id}`);
      showSuccess('Class deleted successfully');
      setShowDeleteModal(false);
      fetchClasses();
    } catch (error) {
      console.error('Error deleting class:', error);
      showError('Failed to delete class');
    }
  };

  // Open students management modal
  const handleManageStudents = async (classItem) => {
    setSelectedClass(classItem);
    await fetchClassStudents(classItem._id);
    await fetchAvailableStudents(classItem._id);
    setSelectedStudents([]);
    setStudentSearchTerm('');
    setShowStudentsModal(true);
  };

  // Handle student selection
  const handleStudentSelection = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  // Add students to class
  const handleAddStudents = async () => {
    if (selectedStudents.length === 0) {
      showError('Please select at least one student');
      return;
    }
    
    try {
      await api.post(`/schools/classes/${selectedClass._id}/students`, {
        studentIds: selectedStudents
      });
      showSuccess('Students added to class successfully');
      await fetchClassStudents(selectedClass._id);
      await fetchAvailableStudents(selectedClass._id);
      setSelectedStudents([]);
    } catch (error) {
      console.error('Error adding students to class:', error);
      showError('Failed to add students to class');
    }
  };

  // Remove student from class
  const handleRemoveStudent = async (studentId) => {
    try {
      await api.delete(`/schools/classes/${selectedClass._id}/students/${studentId}`);
      showSuccess('Student removed from class');
      await fetchClassStudents(selectedClass._id);
      await fetchAvailableStudents(selectedClass._id);
    } catch (error) {
      console.error('Error removing student from class:', error);
      showError('Failed to remove student');
    }
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validationRules = {
      name: { required: true, requiredMessage: 'Class name is required' },
      grade: { required: true, requiredMessage: 'Grade/Year is required' },
      academicYear: { required: true, requiredMessage: 'Academic year is required' }
    };
    
    const { isValid, errors } = validateForm(formData, validationRules);
    
    if (!isValid) {
      setFormErrors(errors);
      return;
    }
    
    try {
      if (formMode === 'add') {
        await api.post('/schools/classes', formData);
        showSuccess('Class added successfully');
      } else {
        await api.put(`/schools/classes/${selectedClass._id}`, formData);
        showSuccess('Class updated successfully');
      }
      
      setShowForm(false);
      fetchClasses();
    } catch (error) {
      console.error('Error saving class:', error);
      showError(error.response?.data?.message || 'Failed to save class');
    }
  };

  // Filter available students based on search term
  const filteredAvailableStudents = studentSearchTerm
    ? availableStudents.filter(
        student =>
          `${student.firstName} ${student.lastName}`.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
          student.admissionNumber.toLowerCase().includes(studentSearchTerm.toLowerCase())
      )
    : availableStudents;

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaUsers className="mr-2 text-primary" /> Class Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage your school's classes and students
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <button
            onClick={handleAddNew}
            className="btn-primary flex items-center"
          >
            <FaPlus className="mr-2" /> Add New Class
          </button>
        </div>
      </div>
      
      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow-soft p-6 mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search classes by name, grade, or academic year..."
            className="form-input pl-10 py-2 w-full"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-400" />
          </div>
        </div>
      </div>
      
      {/* Classes List */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-8 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="space-y-4">
          {filteredClasses.length === 0 ? (
            <div className="bg-white rounded-lg shadow-soft p-8 text-center">
              <FaUsers className="mx-auto text-gray-300 text-5xl mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">
                {searchTerm ? 'No classes match your search' : 'No Classes Added Yet'}
              </h3>
              <p className="text-gray-500 mb-6">
                {searchTerm 
                  ? 'Try adjusting your search terms' 
                  : 'Create classes to organize your students'}
              </p>
              {!searchTerm && (
                <button
                  onClick={handleAddNew}
                  className="btn-primary flex items-center mx-auto"
                >
                  <FaPlus className="mr-2" /> Add Class
                </button>
              )}
            </div>
          ) : (
            <>
              {filteredClasses.map((classItem) => (
                <div 
                  key={classItem._id} 
                  className="bg-white rounded-lg shadow-soft overflow-hidden"
                >
                  <div 
                    className="px-6 py-4 flex justify-between items-center cursor-pointer"
                    onClick={() => toggleClassDetails(classItem._id)}
                  >
                    <div className="flex items-center">
                      <div className="bg-primary-100 p-3 rounded-full mr-4">
                        <FaUsers className="text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{classItem.name}</h3>
                        <div className="text-sm text-gray-500">
                          Grade/Year: {classItem.grade} | Academic Year: {classItem.academicYear} | Students: {classItem.studentCount || 0}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(classItem);
                        }}
                        className="text-gray-500 hover:text-primary p-2"
                        title="Edit Class"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(classItem);
                        }}
                        className="text-gray-500 hover:text-red-500 p-2"
                        title="Delete Class"
                      >
                        <FaTrash />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleManageStudents(classItem);
                        }}
                        className="text-gray-500 hover:text-primary ml-1 p-2"
                        title="Manage Students"
                      >
                        <FaUserGraduate />
                      </button>
                      {expandedClasses[classItem._id] ? (
                        <FaChevronUp className="ml-2 text-gray-500" />
                      ) : (
                        <FaChevronDown className="ml-2 text-gray-500" />
                      )}
                    </div>
                  </div>
                  
                  {expandedClasses[classItem._id] && (
                    <div className="border-t border-gray-200 p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium mb-3">Class Details</h4>
                        
                        <div className="mb-3">
                          <div className="text-sm font-medium text-gray-500">Capacity</div>
                          <div>{classItem.capacity || 'Not specified'}</div>
                        </div>
                        
                        <div className="mb-3">
                          <div className="text-sm font-medium text-gray-500">Class Teacher</div>
                          <div>{classItem.classTeacher 
                            ? `${classItem.classTeacher.firstName} ${classItem.classTeacher.lastName}` 
                            : 'Not assigned'}</div>
                        </div>
                        
                        {classItem.description && (
                          <div className="mb-3">
                            <div className="text-sm font-medium text-gray-500">Description</div>
                            <div className="text-gray-700">{classItem.description}</div>
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <h4 className="font-medium mb-3">Subjects</h4>
                        {classItem.subjects && classItem.subjects.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {classItem.subjects.map((subject, index) => (
                              <span 
                                key={index} 
                                className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                              >
                                {subject}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No subjects specified</p>
                        )}
                        
                        <div className="mt-4 flex justify-end">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleManageStudents(classItem);
                            }}
                            className="btn-primary-outline flex items-center text-sm py-1 px-3"
                          >
                            <FaUserGraduate className="mr-1" /> Manage Students
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      )}
      
      {/* Add/Edit Class Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium">
                {formMode === 'add' ? 'Add New Class' : 'Edit Class'}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Class Name*</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`form-input w-full ${formErrors.name ? 'border-red-500' : ''}`}
                      placeholder="e.g. Class 4A"
                    />
                    {formErrors.name && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Grade/Year*</label>
                    <input
                      type="text"
                      name="grade"
                      value={formData.grade}
                      onChange={handleChange}
                      className={`form-input w-full ${formErrors.grade ? 'border-red-500' : ''}`}
                      placeholder="e.g. Grade 4, Year 10"
                    />
                    {formErrors.grade && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.grade}</p>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Academic Year*</label>
                    <input
                      type="text"
                      name="academicYear"
                      value={formData.academicYear}
                      onChange={handleChange}
                      className={`form-input w-full ${formErrors.academicYear ? 'border-red-500' : ''}`}
                      placeholder="e.g. 2023-2024"
                    />
                    {formErrors.academicYear && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.academicYear}</p>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Capacity</label>
                    <input
                      type="number"
                      name="capacity"
                      value={formData.capacity}
                      onChange={handleChange}
                      min="1"
                      className="form-input w-full"
                      placeholder="Maximum number of students"
                    />
                  </div>
                </div>
                
                <div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Class Teacher</label>
                    <select
                      name="classTeacher"
                      value={formData.classTeacher}
                      onChange={handleChange}
                      className="form-select w-full"
                    >
                      <option value="">Select a teacher</option>
                      {teachers.map(teacher => (
                        <option key={teacher._id} value={teacher._id}>
                          {teacher.firstName} {teacher.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Subjects (comma-separated)</label>
                    <input
                      type="text"
                      name="subjects"
                      value={formData.subjects.join(', ')}
                      onChange={(e) => {
                        const subjectsArray = e.target.value
                          .split(',')
                          .map(subject => subject.trim())
                          .filter(subject => subject !== '');
                        setFormData({
                          ...formData,
                          subjects: subjectsArray
                        });
                      }}
                      className="form-input w-full"
                      placeholder="e.g. Mathematics, English, Science"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows="4"
                      className="form-textarea w-full"
                      placeholder="Additional information about the class"
                    ></textarea>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6 border-t border-gray-200 pt-6">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {formMode === 'add' ? 'Add Class' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Student Management Modal */}
      <Modal
        isOpen={showStudentsModal}
        onClose={() => setShowStudentsModal(false)}
        title={selectedClass ? `Manage Students - ${selectedClass.name || ''}` : 'Manage Students'}
        size="xl"
      >
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Current Students List */}
            <div>
              <h3 className="font-medium text-lg mb-4 flex items-center">
                <FaUserGraduate className="mr-2 text-primary" /> Current Students
              </h3>
              
              {students.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-6 text-center">
                  <p className="text-gray-500">No students in this class yet</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                  {students.map((student) => (
                    <div 
                      key={student._id} 
                      className="bg-white border border-gray-200 rounded-lg p-3 flex justify-between items-center"
                    >
                      <div>
                        <div className="font-medium">
                          {student.firstName} {student.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {student.admissionNumber}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveStudent(student._id)}
                        className="text-red-500 hover:text-red-700"
                        title="Remove from class"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Available Students */}
            <div>
              <h3 className="font-medium text-lg mb-4">Add Students</h3>
              
              <div className="mb-4 relative">
                <input
                  type="text"
                  placeholder="Search students by name or ID..."
                  className="form-input pl-10 py-2 w-full"
                  value={studentSearchTerm}
                  onChange={handleStudentSearchChange}
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="text-gray-400" />
                </div>
              </div>
              
              {filteredAvailableStudents.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-6 text-center">
                  <p className="text-gray-500">No available students to add</p>
                </div>
              ) : (
                <>
                  <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
                    {filteredAvailableStudents.map((student) => (
                      <div 
                        key={student._id} 
                        className="p-3 border-b border-gray-200 last:border-b-0 flex items-center"
                      >
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student._id)}
                          onChange={() => handleStudentSelection(student._id)}
                          className="form-checkbox mr-3"
                        />
                        <div>
                          <div className="font-medium">
                            {student.firstName} {student.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {student.admissionNumber}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={handleAddStudents}
                      className="btn-primary"
                      disabled={selectedStudents.length === 0}
                    >
                      Add Selected Students
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirm Deletion"
        size="sm"
      >
        <div className="p-6">
          <p className="mb-4">
            Are you sure you want to delete the class "{selectedClass ? selectedClass.name : ''}"?
            This action cannot be undone.
          </p>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="btn-danger"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClassManagement;
